import { useState } from 'react';
import { CheckIcon, DuplicateIcon } from '@heroicons/react/outline';
import toast from 'react-hot-toast';

import {
  useExternalPractitionerFormInvitesForContactQuery,
  ExternalPractitionerFormInviteStatus,
  PatientContact,
} from '../../../../generated/graphql';
import { getContactFullName } from '../../../pages/PatientProfile/Account/Contacts/constants';
import ModalDialog from '../../ModalDialog';
import Button from '../../Button';

type ViewLinksModalProps = {
  isOpen: boolean;
  setClosed: () => void;
  contact: PatientContact | null;
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const date = new Date(value);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function StatusBadge({
  status,
}: {
  status: ExternalPractitionerFormInviteStatus;
}) {
  const statusConfig = {
    [ExternalPractitionerFormInviteStatus.Pending]: {
      label: 'Pending',
      className: 'bg-orange-25',
    },
    [ExternalPractitionerFormInviteStatus.Completed]: {
      label: 'Completed',
      className: 'bg-green-25',
    },
    [ExternalPractitionerFormInviteStatus.Expired]: {
      label: 'Expired',
      className: 'bg-neutral-50',
    },
    [ExternalPractitionerFormInviteStatus.Revoked]: {
      label: 'Revoked',
      className: 'bg-neutral-50',
    },
  };
  const config =
    statusConfig[status] ??
    statusConfig[ExternalPractitionerFormInviteStatus.Pending];
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-caption text-neutral-150 ${config.className}`}
    >
      {config.label}
    </span>
  );
}

export default function ViewLinksModal({
  isOpen,
  setClosed,
  contact,
}: ViewLinksModalProps) {
  const { data, loading } = useExternalPractitionerFormInvitesForContactQuery({
    variables: { contactId: contact?.id ?? '' },
    skip: !isOpen || !contact?.id,
  });

  const invites = data?.externalPractitionerFormInvitesForContact ?? [];
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null);

  const handleCopyLink = async (inviteId: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
      setCopiedInviteId(inviteId);
      setTimeout(() => setCopiedInviteId(null), 3000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <ModalDialog
      title={`Forms sent to ${
        contact ? getContactFullName(contact) : 'this contact'
      }`}
      isOpen={isOpen}
      setClosed={setClosed}
      hideFooter
      width="medium"
      overflow="auto"
    >
      <div className="flex flex-col gap-4">
        {loading ? (
          <div className="py-8 text-center text-caption text-neutral-125">
            Loading...
          </div>
        ) : invites.length === 0 ? (
          <p className="py-6 text-center text-caption text-neutral-125">
            No form links have been sent to this contact yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {invites.map((invite) => (
              <li
                key={invite.id}
                className="flex flex-col gap-2 rounded border border-neutral-50 bg-neutral-50 p-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-body font-medium text-neutral-150">
                      {invite.formName}
                    </p>
                    <p className="mt-1 text-small-caption text-neutral-125">
                      Sent {formatDate(invite.sentAt)} · Expires{' '}
                      {formatDate(invite.expiresAt)}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      title="Copy link"
                      size="small"
                      theme="secondary"
                      IconComponent={
                        copiedInviteId === invite.id ? CheckIcon : DuplicateIcon
                      }
                      iconPosition="left"
                      iconClassName={
                        copiedInviteId === invite.id
                          ? 'text-green-150'
                          : undefined
                      }
                      onClick={() => handleCopyLink(invite.id, invite.url)}
                    />
                    <StatusBadge status={invite.status} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ModalDialog>
  );
}
